// BySourceTab — the "By Source" holdings view, matching the Participant Redesign
// standalone (portfolio3.jsx BySourceTab).
//
// One collapsed row per money source, because a 7-source account (pre-tax, match,
// Roth, profit sharing, after-tax, safe harbor, rollover) as seven expanded cards
// buries the one thing the row is for: how much of the balance came from where,
// and whether it's yours yet. The row states that much — name, vesting, share,
// balance — and the detail (description, YTD/ITD/growth, per-fund split) opens on
// demand.
//
// Vesting is on the ROW rather than in the stat strip: "is this money actually
// mine?" is the question a source raises, and burying it one click deep answers it
// only for people who already thought to ask.
//
// The per-fund split is the holdings tree, so a managed model is one row that
// expands to its members. Each line is the fund's real balance inside the source and
// its share of that source (FM-3085), printed as sent: the source total is their sum
// (FM-3064). A payload without per-source funds falls back to each position scaled to
// the source's share, split in whole cents that add up to the source's balance.
//
// An OPEN model reads as its own object inside the panel: a framed container whose
// header is the model row and whose body is its members — indented to start under the
// model's own name, their colour dots dimmed — closed by a note naming the model. A fund
// held inside a model and a fund held directly by the source must not look alike.
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Money, EmptyState } from '../../../reusable';
import { cx } from '../../../../utils/cx';
import { formatCurrency, formatSignedCurrency } from '../../../../utils/format';
import { treeOrFlat, sourceHoldings } from '../portfolio.models';
import { sourcePctText, allocateCents } from '../portfolio.adapters';
import { ModelTag, useModelExpand } from './ModelRow';
import './by-source.css';

// Plus that becomes a minus — the same expand affordance the holdings models use.
function PlusMinus({ open, small }) {
  return (
    <span className={cx('by-source__chev', small && 'by-source__chev--sm')} aria-hidden="true">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        {!open && <line x1="12" y1="5" x2="12" y2="19" />}
      </svg>
    </span>
  );
}
PlusMinus.propTypes = { open: PropTypes.bool, small: PropTypes.bool };

// One fund line of a source's split: the fund's share of this source.
function FundLine({ f, amount, child }) {
  return (
    <div className={cx('by-source__fund', child && 'by-source__fund--child')}>
      <span className="by-source__dot" style={{ background: f.color }} />
      <span className="by-source__fund-nm">{f.name}</span>
      <span className="by-source__fund-tkr">{f.ticker}</span>
      <span className="by-source__fund-bar">
        <span className={cx(amount > 0 && 'is-held')} style={{ width: `${f.pct}%`, background: f.color }} />
      </span>
      <span className="by-source__fund-pct">{sourcePctText(f.pct, amount)}</span>
      <Money className="by-source__fund-bal" value={amount} size="sm" />
    </div>
  );
}
FundLine.propTypes = { f: PropTypes.object.isRequired, amount: PropTypes.number.isRequired, child: PropTypes.bool };

export function BySourceTab({ sources: allSources = [], funds = [], tree, total }) {
  const { t } = useTranslation();
  const { isOpen: sourceOpen, toggle, openKeys, setOpenKeys } = useModelExpand();
  // Models are keyed per source, so opening one inside Pre-Tax leaves it closed in Roth.
  const { isOpen: modelOpen, toggle: toggleModel } = useModelExpand();
  const items = useMemo(() => treeOrFlat(tree, funds), [tree, funds]);
  // A source holding $0.00 is hidden, as Classic does (FM-3064).
  const sources = useMemo(() => allSources.filter((s) => Math.round((s.total || 0) * 100) !== 0), [allSources]);

  if (!sources.length) {
    return <EmptyState title={t('portfolio.bySource.emptyTitle')} message={t('portfolio.bySource.emptyMessage')} />;
  }

  // Prefer the plan's own balance; fall back to what the sources add up to so the
  // shares still total 100% when no balance was passed.
  const planTotal = total || sources.reduce((s, x) => s + (x.total || 0), 0);

  const allOpen = openKeys.size === sources.length;
  const toggleAll = () => setOpenKeys(allOpen ? new Set() : new Set(sources.map((s) => s.key)));

  // "Fully vested" / "50% vested" / "Not yet vested", plus the plan's schedule note
  // when it ships one ("3-year cliff · vests Mar 2027"). Null when nothing is known —
  // silence beats asserting a vesting status we can't back up.
  const vestingText = (s) => {
    const pct = s.vested;
    if (typeof pct !== 'number') return null;
    const note = s.vestingNote ? ` · ${s.vestingNote}` : '';
    if (pct >= 100) return t('portfolio.bySource.fullyVested');
    if (pct <= 0) return `${t('portfolio.bySource.notYetVested')}${note}`;
    return `${t('portfolio.bySource.pctVested', { pct })}${note}`;
  };

  return (
    <div className="by-source">
      <div className="by-source__toolbar">
        <span className="by-source__count">
          {t(sources.length === 1 ? 'portfolio.bySource.countOne' : 'portfolio.bySource.countMany', {
            count: sources.length,
            total: formatCurrency(planTotal),
          })}
        </span>
        <button type="button" className="by-source__toggle-all" onClick={toggleAll}>
          {t(allOpen ? 'portfolio.bySource.collapseAll' : 'portfolio.bySource.expandAll')}
        </button>
      </div>

      {/* Says which percentage this view is showing — the same disambiguation the
          By-Fund columns make explicit. */}
      <p className="by-source__note">{t('portfolio.bySource.balanceNote')}</p>

      {sources.map((s) => {
        const isOpen = sourceOpen(s.key);
        // Two decimals, as Classic prints it (FM-3085).
        const sharePct = planTotal ? Number(((s.total / planTotal) * 100).toFixed(2)) : 0;
        const hasLines = Array.isArray(s.funds) && s.funds.length > 0;
        const lines = hasLines ? sourceHoldings(items, s.funds, s.total) : items;
        // Real lines print their own cents; the account-wide fallback is scaled to the source.
        const amounts = hasLines ? lines.map((it) => it.balance) : allocateCents(s.total, lines.map((it) => it.balance));
        const vest = vestingText(s);
        const panelId = `by-source-${s.key}`;

        return (
          <div key={s.key} className={cx('by-source__item', isOpen && 'is-open')}>
            <button
              type="button"
              className="by-source__row"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(s.key)}
            >
              <span className="by-source__sw" style={{ background: s.color }} />
              <span className="by-source__name">{s.name}</span>
              {vest && (
                <span className={cx('by-source__vest', s.vested < 100 && 'is-partial')}>{vest}</span>
              )}
              <span className="by-source__pct">
                {t('portfolio.bySource.ofBalance', { pct: sharePct })}
              </span>
              <span className="by-source__bal">{formatCurrency(s.total)}</span>
              <PlusMinus open={isOpen} />
            </button>

            {isOpen && (
              <div
                className="by-source__body"
                id={panelId}
                role="region"
                aria-label={t('portfolio.bySource.detailAria', { name: s.name })}
              >
                {s.desc && <div className="by-source__desc">{s.desc}</div>}

                <div className="by-source__stats">
                  {/* Contributions in are whole dollars — they arrive as round payroll
                      amounts, and the cents on them were noise beside the growth figure,
                      which keeps its cents because it is a computed number. */}
                  <div className="by-source__stat">
                    <span className="by-source__stat-lbl">{t('portfolio.bySource.ytdIn')}</span>
                    {/* A real 0 means nothing came in this year — an em dash reads as
                        "none", where "$0" reads as a measured figure. */}
                    <span className="by-source__stat-val">
                      {s.ytd === 0 ? '—' : formatCurrency(s.ytd, false)}
                    </span>
                  </div>
                  <div className="by-source__stat">
                    <span className="by-source__stat-lbl">{t('portfolio.bySource.itdIn')}</span>
                    <span className="by-source__stat-val">{formatCurrency(s.contrib, false)}</span>
                  </div>
                  <div className="by-source__stat">
                    <span className="by-source__stat-lbl">{t('portfolio.bySource.growth')}</span>
                    <span className={cx('by-source__stat-val', s.growth < 0 ? 'by-source__stat-val--neg' : 'by-source__stat-val--pos')}>
                      {formatSignedCurrency(s.growth)}
                    </span>
                  </div>
                </div>

                <div className="by-source__funds">
                  {lines.map((it, i) => {
                    if (it.type !== 'model') return <FundLine key={it.key} f={it} amount={amounts[i]} />;
                    const children = it.children || [];
                    const childAmounts = hasLines ? children.map((c) => c.balance) : allocateCents(amounts[i], children.map((c) => c.balance));
                    const mKey = `${s.key}:${it.key}`;
                    const mOpen = modelOpen(mKey);
                    const groupId = `${panelId}-${it.key}`;
                    return (
                      <div key={it.key} className={cx('by-source__model', mOpen && 'is-open')}>
                        <button
                          type="button"
                          className="by-source__fund by-source__fund--model"
                          aria-expanded={mOpen}
                          aria-controls={groupId}
                          aria-label={t(mOpen ? 'portfolio.byFund.collapseModel' : 'portfolio.byFund.expandModel', { name: it.name })}
                          onClick={() => toggleModel(mKey)}
                        >
                          <span className="by-source__dot" style={{ background: it.color }} />
                          <span className="by-source__fund-nm">
                            <PlusMinus open={mOpen} small />
                            {it.name}
                            <ModelTag t={t} />
                          </span>
                          <span className="by-source__fund-tkr">—</span>
                          <span className="by-source__fund-bar">
                            <span className={cx(amounts[i] > 0 && 'is-held')} style={{ width: `${it.balancePct}%`, background: it.color }} />
                          </span>
                          <span className="by-source__fund-pct">{sourcePctText(it.balancePct, amounts[i])}</span>
                          <Money className="by-source__fund-bal" value={amounts[i]} size="sm" />
                        </button>
                        {/* An open model is its own little CONTAINER: the model row is the
                            header, its members are the body, and a note naming the model
                            closes it — which is what makes "held inside this model"
                            different from "held directly by this source". Without it the
                            members were indented rows on the same white panel, so where
                            the model's list ended and the source's own next holding began
                            was a guess.
                            The frame is drawn on the wrapper (CSS, `is-open`). Two earlier
                            attempts are worth not repeating: a shaded band read as a grey
                            slab across twenty rows and swallowed the share bars, whose
                            track is that same colour; a coloured rail down the side was
                            one stray vertical line in a card that has none. */}
                        {mOpen && (
                          <div
                            id={groupId}
                            role="group"
                            className="by-source__model-funds"
                            aria-label={t('portfolio.bySource.modelGroupAria', { name: it.name })}
                          >
                            {children.map((c, j) => <FundLine key={c.ticker} f={c} amount={childAmounts[j]} child />)}
                            <p className="by-source__model-note">
                              {t('portfolio.byFund.modelNote', { name: it.name, desc: it.desc || '' }).trim()}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

BySourceTab.propTypes = {
  sources: PropTypes.array,
  funds: PropTypes.array,
  /** Model-aware holdings list; falls back to one line per fund when absent. */
  tree: PropTypes.array,
  /** Plan balance, for each source's share of it. Falls back to the sources' sum. */
  total: PropTypes.number,
};

export default BySourceTab;
